import { IcAdressContact, IcMail, IcPhoneStroke, IcPlus, IcTrash } from '@assets/icons/24x24';
import {
  createListCollection,
  Field as ChakraField,
  Fieldset,
  For,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import { PlatformForm } from '@components/cockpit/panels/platformManagement/PlatformFormContainer';
import { CustomSelect } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { CONTACT_LOCALES, CONTACT_TIMEZONES, getBrowserContactLocale, getBrowserTimeZone } from '@utils/locale.utils';
import { isInvalid } from '@utils/validation/validation.utils';
import { Field, FieldArray, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Contact, MessageRequestMediaEnum, MessageRequestTemplateEnum } from '@/types/chat/chat.types';

const timezoneCollection = createListCollection({
  items: [...new Set([...CONTACT_TIMEZONES, getBrowserTimeZone()])].map((value) => ({ value, label: value })),
});

const emptyContact: Partial<Contact> = {
  site: 'local',
  name: '',
  contact: '',
  active: true,
  subscriptions: [
    MessageRequestTemplateEnum.NFZ_ENTRY,
    MessageRequestTemplateEnum.NFZ_EXIT,
    MessageRequestTemplateEnum.DRONE_LOST,
  ],
  locale: getBrowserContactLocale(),
  zone: getBrowserTimeZone(),
};

const createNewContact = (media: MessageRequestMediaEnum, platformCode?: string) => ({
  ...emptyContact,
  code: uuidv4(),
  media,
  platform: platformCode,
});

type Props = {
  index: number;
  media: MessageRequestMediaEnum;
};

function ContactRow({ index, media }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { errors, touched, values, setFieldValue } = useFormikContext<Partial<PlatformForm>>();
  const isEmail = media === MessageRequestMediaEnum.EMAIL;
  const contact = values.contacts?.[index];
  const localeCollection = createListCollection({
    items: CONTACT_LOCALES.map((value) => ({
      value,
      label: formatMessage({ id: `components.userSettings.language.${value}` }),
    })),
  });

  return (
    <VStack width="100%" gap={2} alignItems="start">
      <HStack width="100%" gap={2}>
        <ChakraField.Root variant="cockpit" required invalid={isInvalid(`contacts[${index}].name`, touched, errors)}>
          <InputGroup
            startElement={<Icon as={IcAdressContact} color="neutral.500" />}
            startElementProps={{ paddingLeft: 2, paddingRight: 1.5 }}
          >
            <Field
              as={Input}
              type="text"
              required
              placeholder={formatMessage({ id: 'components.platforms.diffusionLists.name' })}
              name={`contacts[${index}].name`}
              id={`contacts-${index}-name`}
              style={{ paddingInlineStart: '52px' }}
            />
          </InputGroup>
        </ChakraField.Root>
        <ChakraField.Root variant="cockpit" required invalid={isInvalid(`contacts[${index}].contact`, touched, errors)}>
          <InputGroup
            startElement={<Icon as={isEmail ? IcMail : IcPhoneStroke} color="neutral.500" />}
            startElementProps={{ paddingLeft: 2, paddingRight: 1.5 }}
            endElement={
              <Switch
                size="sm"
                marginLeft="auto"
                checked={contact?.active}
                onCheckedChange={() => {
                  setFieldValue(`contacts[${index}].active`, !contact?.active);
                }}
              />
            }
            endElementProps={{ paddingLeft: 1.5, paddingRight: 2 }}
          >
            <Field
              as={Input}
              type="text"
              placeholder={formatMessage({ id: `components.platforms.diffusionLists.${isEmail ? 'email' : 'phone'}` })}
              name={`contacts[${index}].contact`}
              id={`contacts-${index}-contact`}
              style={{ paddingLeft: '52px', paddingRight: '62px' }}
            />
          </InputGroup>
        </ChakraField.Root>
      </HStack>
      <HStack width="100%" gap={2}>
        <CustomSelect
          name={`contacts[${index}].locale`}
          size="sm"
          width="50%"
          collection={localeCollection}
          value={contact?.locale}
          onChange={(value) => setFieldValue(`contacts[${index}].locale`, value)}
        />
        <CustomSelect
          name={`contacts[${index}].zone`}
          size="sm"
          width="50%"
          collection={timezoneCollection}
          value={contact?.zone}
          onChange={(value) => setFieldValue(`contacts[${index}].zone`, value)}
        />
      </HStack>
    </VStack>
  );
}

type ContactTabContentProps = {
  mediaType: MessageRequestMediaEnum;
  contacts?: Contact[];
  platformCode?: string;
};

function ContactTabContent({ mediaType, contacts, platformCode }: Readonly<ContactTabContentProps>) {
  return (
    <Tabs.Content value={mediaType === MessageRequestMediaEnum.EMAIL ? 'email' : 'phone'} backgroundColor="neutral.700">
      <FieldArray name="contacts">
        {({ push, remove }) => (
          <VStack width="100%" gap={2} alignItems="start">
            <For each={contacts}>
              {(contact, index) => {
                if (contact.media !== mediaType) {
                  return null;
                }
                return (
                  <HStack width="100%" gap={2} key={contact.code} alignItems="start">
                    <ContactRow index={index} media={mediaType} />
                    <IconButton variant="pick" aria-label="delete" onClick={() => remove(index)}>
                      <Icon as={IcTrash} boxSize="24px" />
                    </IconButton>
                  </HStack>
                );
              }}
            </For>
            <IconButton variant="pick" aria-label="add" onClick={() => push(createNewContact(mediaType, platformCode))}>
              <Icon as={IcPlus} boxSize="24px" />
            </IconButton>
          </VStack>
        )}
      </FieldArray>
    </Tabs.Content>
  );
}

export default function PlatformDiffusionListForm() {
  const { errors, touched, values } = useFormikContext<Partial<PlatformForm>>();
  const { formatMessage } = useIntl();

  return (
    <CockpitFormLayout
      title="components.platforms.diffusionLists.title"
      width={760}
      tooltipLabel={formatMessage({ id: 'components.platforms.diffusionLists.help' })}
    >
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <Tabs.Root variant="light" size="md" gap={0.25} backgroundColor="neutral.700" defaultValue="email">
          <Tabs.List backgroundColor="neutral.700" justifyContent="start" gap={4}>
            <Tabs.Trigger value="email" backgroundColor="neutral.700">
              <Text>
                <FormattedMessage id="components.platforms.diffusionLists.email" />
              </Text>
            </Tabs.Trigger>
            <Tabs.Trigger value="phone" backgroundColor="neutral.700">
              <Text>
                <FormattedMessage id="components.platforms.diffusionLists.phone" />
              </Text>
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.ContentGroup>
            <ContactTabContent
              mediaType={MessageRequestMediaEnum.EMAIL}
              contacts={values.contacts}
              platformCode={values.code}
            />
            <ContactTabContent
              mediaType={MessageRequestMediaEnum.SMS}
              contacts={values.contacts}
              platformCode={values.code}
            />
          </Tabs.ContentGroup>
        </Tabs.Root>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
